import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, Search, X, Loader2 } from 'lucide-react';
import { RecipeAllergenCard } from '@/components/recipes/RecipeAllergenCard';
import { SpoonacularTagSummary } from '@/components/recipes/SpoonacularTagSummary';
import { useSpoonacularTagEnrichment } from '@/hooks/useSpoonacularTagEnrichment';
import type {
  SpoonacularTagClassificationSource,
  SpoonacularTagClassificationStatus,
} from '@/lib/apiService';

// Recipe search result type (for backward compatibility)
export interface RecipeSearchResult {
  id: string;
  title?: string;
  name?: string;
  image?: string;
  readyInMinutes?: number;
  servings?: number;
  defaultServing?: number;
  allowedServings?: number[];
  calories?: number;
  protein?: number;
  carbs?: number;
  fat?: number;
  allergens?: string[];
  allergenTagIds?: string[];
  dietaryTagIds?: string[];
  dietLabels?: string[];
  intoleranceTagIds?: string[];
  intoleranceTags?: string[];
  tagClassificationStatus?: SpoonacularTagClassificationStatus;
  tagClassificationSource?: SpoonacularTagClassificationSource;
  tagClassificationUpdatedAt?: string | null;
  source?: string;
  sourceId?: string;
  provider?: string;
  spoonacularId?: string;
  [key: string]: any;
}

export interface SearchableDropdownProps<T = any> {
  // Required props
  value: string;
  onChange: (value: string) => void;
  onSelect: (item: T) => void;
  searchResults: T[];
  isSearching?: boolean;
  
  // Optional customization props
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  containerClassName?: string;
  dropdownClassName?: string;
  resultsPosition?: 'absolute' | 'static';
  portalResults?: boolean;
  showClearButton?: boolean;
  minSearchLength?: number;
  disabled?: boolean;
  showResults?: boolean; // Control dropdown visibility externally
  
  // Callback props
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  onClose?: () => void; // Called when dropdown should close (for externally controlled)
  onSearchSubmit?: () => void;
  
  // Loading/adding state
  isAdding?: boolean;
  addingItemId?: string | null;
  isLoadingMore?: boolean; // Show loading indicator at bottom of results
  
  // Custom render props
  getItemId?: (item: T) => string;
  getItemDisplayName?: (item: T) => string;
  renderResult?: (item: T, index: number, isSelected: boolean) => React.ReactNode;
  renderEmptyState?: () => React.ReactNode;
  renderLoadingState?: () => React.ReactNode;
  emptyStateText?: string;
  searchResultsHeader?: string;
  
  // Color theme customization
  colorPrefix?: 'blue' | 'green' | 'purple' | 'red' | 'orange'; // Color prefix for theming (default: 'blue')
}

export function SearchableDropdown<T = any>({
  value,
  onChange,
  onSelect,
  searchResults = [],
  isSearching = false,
  placeholder = "🔍 Search...",
  className = "",
  inputClassName = "",
  containerClassName = "",
  dropdownClassName = "",
  resultsPosition = 'absolute',
  portalResults = false,
  showClearButton = true,
  minSearchLength = 3,
  disabled = false,
  showResults: externalShowResults,
  onFocus,
  onBlur,
  onClose,
  onSearchSubmit,
  isAdding = false,
  addingItemId,
  isLoadingMore = false,
  getItemId = (item: any) => item.id || item.name || String(item),
  getItemDisplayName = (item: any) => item.title || item.name || String(item),
  renderResult,
  renderEmptyState,
  renderLoadingState,
  emptyStateText = "No results found",
  searchResultsHeader = "Search Results",
  colorPrefix = 'blue',
}: SearchableDropdownProps<T>) {
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [internalShowResults, setInternalShowResults] = useState(false);
  
  const resultsListRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});
  
  // Determine if showResults is controlled externally
  const isResultsControlled = externalShowResults !== undefined;
  const showResults = isResultsControlled ? externalShowResults : internalShowResults;
  const enrichedSearchResults = useSpoonacularTagEnrichment(
    searchResults as Array<T & Record<string, any>>,
    { enabled: showResults && !isSearching },
  ) as T[];
  
  // Calculate if any item is currently being added
  // Check if addingItemId exists (item is being added) or isAdding flag is set
  const isAnyItemAdding = isAdding || !!addingItemId;
  
  // Show results when there are search results and search term is long enough
  useEffect(() => {
    if (!isResultsControlled) {
      if (value.length >= minSearchLength && searchResults.length > 0) {
        setInternalShowResults(true);
      } else if (value.length < minSearchLength) {
        setInternalShowResults(false);
      }
    }
  }, [value, searchResults, minSearchLength, isResultsControlled]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && resultsListRef.current && showResults) {
      const selectedElement = resultsListRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }
  }, [selectedIndex, showResults]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current || !showResults) return;
      
      const target = event.target as HTMLElement;
      
      // Check if click is inside the container (input field or dropdown)
      const isInsideContainer = containerRef.current.contains(target);
      const isInsideDropdown = !!dropdownRef.current?.contains(target);
      // Allow interactions with portaled controls (e.g. Radix Select) owned by this dropdown.
      const isInsideInteractivePortal = !!target.closest('[data-search-dropdown-interactive="true"]');
      
      // Close if click is outside the container
      if (!isInsideContainer && !isInsideDropdown && !isInsideInteractivePortal) {
        if (!isResultsControlled) {
          setInternalShowResults(false);
        } else {
          // For externally controlled, notify parent to close
          // Parent will handle preventing auto-reopening
          onClose?.();
        }
        setSelectedIndex(-1);
      }
    };

    if (showResults) {
      // Use capture phase (true) to ensure this fires before Dialog/Modal handlers
      // This ensures the dropdown closes even when clicking outside in modals
      document.addEventListener('mousedown', handleClickOutside, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    }
  }, [showResults, isResultsControlled, onClose]);

  // Keep portaled dropdown anchored to the input while scrolling/resizing
  useEffect(() => {
    if (!portalResults || !showResults) return;

    const updatePortalPosition = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setPortalStyle({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width,
      });
    };

    updatePortalPosition();
    window.addEventListener('resize', updatePortalPosition);
    window.addEventListener('scroll', updatePortalPosition, true);

    return () => {
      window.removeEventListener('resize', updatePortalPosition);
      window.removeEventListener('scroll', updatePortalPosition, true);
    };
  }, [portalResults, showResults]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setSelectedIndex(-1);
  };

  // Handle input focus
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (value.length >= minSearchLength && searchResults.length > 0) {
      if (!isResultsControlled) {
        setInternalShowResults(true);
      }
    }
    onFocus?.(event);
  };

  // Handle input blur
  const handleBlur = () => {
    // Note: Click-outside handler will handle closing, so we just call onBlur callback
      onBlur?.();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showResults || enrichedSearchResults.length === 0) {
      if (e.key === 'Enter' && onSearchSubmit && value.trim().length >= minSearchLength) {
        e.preventDefault();
        onSearchSubmit();
      }
      return;
    }
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < enrichedSearchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : enrichedSearchResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < enrichedSearchResults.length) {
          const selectedItem = enrichedSearchResults[selectedIndex];
          if (selectedItem && !isAdding && !addingItemId) {
            onSelect(selectedItem);
            setSelectedIndex(-1);
          }
        } else if (onSearchSubmit) {
          onSearchSubmit();
        }
        break;
      case 'Escape':
        e.preventDefault();
        if (!isResultsControlled) {
          setInternalShowResults(false);
        }
        setSelectedIndex(-1);
        break;
    }
  };

  // Handle clear button
  const handleClear = () => {
    onChange('');
    setSelectedIndex(-1);
  };

  // Color classes based on theme
  const getColorClasses = () => {
    const colors = {
      blue: {
        bg50: 'bg-blue-50',
        bg100: 'bg-blue-100',
        hoverBg50: 'hover:bg-blue-50',
        bgGradient: 'from-blue-50 to-blue-100',
        border500: 'border-blue-500',
        border200: 'border-blue-200',
        border400: 'border-blue-400',
        ring: 'ring-blue-100',
        focusRing: 'focus:ring-blue-100',
        focusBorder: 'focus:border-blue-400',
        text600: 'text-blue-600',
        text500: 'text-blue-500',
        text700: 'text-blue-700',
      },
      green: {
        bg50: 'bg-green-50',
        bg100: 'bg-green-100',
        hoverBg50: 'hover:bg-green-50',
        bgGradient: 'from-green-50 to-green-100',
        border500: 'border-green-500',
        border200: 'border-green-200',
        border400: 'border-green-400',
        ring: 'ring-green-100',
        focusRing: 'focus:ring-green-100',
        focusBorder: 'focus:border-green-400',
        text600: 'text-green-600',
        text500: 'text-green-500',
        text700: 'text-green-700',
      },
      purple: {
        bg50: 'bg-purple-50',
        bg100: 'bg-purple-100',
        hoverBg50: 'hover:bg-purple-50',
        bgGradient: 'from-purple-50 to-purple-100',
        border500: 'border-purple-500',
        border200: 'border-purple-200',
        border400: 'border-purple-400',
        ring: 'ring-purple-100',
        focusRing: 'focus:ring-purple-100',
        focusBorder: 'focus:border-purple-400',
        text600: 'text-purple-600',
        text500: 'text-purple-500',
        text700: 'text-purple-700',
      },
      red: {
        bg50: 'bg-red-50',
        bg100: 'bg-red-100',
        hoverBg50: 'hover:bg-red-50',
        bgGradient: 'from-red-50 to-red-100',
        border500: 'border-red-500',
        border200: 'border-red-200',
        border400: 'border-red-400',
        ring: 'ring-red-100',
        focusRing: 'focus:ring-red-100',
        focusBorder: 'focus:border-red-400',
        text600: 'text-red-600',
        text500: 'text-red-500',
        text700: 'text-red-700',
      },
      orange: {
        bg50: 'bg-orange-50',
        bg100: 'bg-orange-100',
        hoverBg50: 'hover:bg-orange-50',
        bgGradient: 'from-orange-50 to-orange-100',
        border500: 'border-orange-500',
        border200: 'border-orange-200',
        border400: 'border-orange-400',
        ring: 'ring-orange-100',
        focusRing: 'focus:ring-orange-100',
        focusBorder: 'focus:border-orange-400',
        text600: 'text-orange-600',
        text500: 'text-orange-500',
        text700: 'text-orange-700',
      },
    };
    return colors[colorPrefix];
  };

  const colorClasses = getColorClasses();
  const dropdownPositionClassName =
    resultsPosition === 'static' ? 'relative' : 'absolute left-0 z-[9999] min-w-full';
  const canSubmitSearch = Boolean(onSearchSubmit)
    && value.trim().length >= minSearchLength
    && !disabled
    && !isAnyItemAdding;

  const renderDropdownResults = () => (
    <div
      ref={dropdownRef}
      data-dropdown-results
      className={`bg-white border-2 ${colorClasses.border200} rounded-lg shadow-xl max-h-64 overflow-y-auto ${dropdownClassName}`}
    >
      <div className={`px-3 py-2 ${colorClasses.bg50} border-b ${colorClasses.border200}`}>
        <p className={`text-xs font-medium ${colorClasses.text700}`}>{searchResultsHeader}</p>
      </div>
      
      {isSearching ? (
        renderLoadingState ? renderLoadingState() : defaultRenderLoadingState()
      ) : !Array.isArray(enrichedSearchResults) || enrichedSearchResults.length === 0 ? (
        renderEmptyState ? renderEmptyState() : defaultRenderEmptyState()
      ) : (
        <>
          <ul ref={resultsListRef} className="divide-y">
            {enrichedSearchResults.map((item, index) =>
              renderResult ? 
                renderResult(item, index, selectedIndex === index) : 
                defaultRenderResult(item, index, selectedIndex === index)
            )}
          </ul>
          {isLoadingMore && (
            <div className="px-3 py-2 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading more results...</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  const getDomMetadataForItem = (item: T, fallbackItemId: string) => {
    const itemRecord = item as Record<string, unknown>;
    const id = String(itemRecord.id ?? fallbackItemId ?? '').trim();
    const type = String(itemRecord.type ?? itemRecord._searchItemType ?? '').trim();
    const source = String(itemRecord.source ?? itemRecord._rawSource ?? '').trim();
    const provider = String(itemRecord.provider ?? '').trim();
    const sourceId = String(itemRecord.sourceId ?? itemRecord._sourceId ?? '').trim();
    const internalId = String(itemRecord.internalId ?? itemRecord.internalIngredientId ?? '').trim();
    const spoonacularId = String(itemRecord.spoonacularId ?? itemRecord.spoonacularIngredientId ?? '').trim();
    const rawIsCached = itemRecord.isCached;
    const isCached =
      typeof rawIsCached === 'boolean'
        ? rawIsCached
        : source.toLowerCase() === 'cached';

    return {
      id,
      type,
      source,
      provider,
      isCached,
      sourceId,
      internalId,
      spoonacularId,
    };
  };

  // Default render functions
  const defaultRenderResult = (item: T, index: number, isSelected: boolean) => {
    const itemId = getItemId(item);
    const displayName = getItemDisplayName(item);
    const domMetadata = getDomMetadataForItem(item, itemId);
    const isAddingItem = addingItemId === itemId || isAdding;
    const itemRecord = item as Record<string, unknown>;
    const itemWithImage = item as { image?: string; imageUrl?: string };
    const imageSrc = typeof itemWithImage.image === 'string' && itemWithImage.image.trim().length > 0
      ? itemWithImage.image
      : typeof itemWithImage.imageUrl === 'string' && itemWithImage.imageUrl.trim().length > 0
        ? itemWithImage.imageUrl
        : '';
    const nutritionRecord =
      itemRecord.nutrition && typeof itemRecord.nutrition === 'object'
        ? (itemRecord.nutrition as Record<string, unknown>)
        : undefined;
    const toOptionalNumber = (value: unknown): number | undefined => {
      if (value == null || value === '') return undefined;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    };
    const formatFactor = (label: string, value: unknown): string | null => {
      const numeric = toOptionalNumber(value);
      if (numeric === undefined || numeric <= 0) return null;
      const percent = numeric * 100;
      const formatted = Number.isInteger(percent) ? String(percent) : percent.toFixed(1);
      return `${label} ${formatted}%`;
    };
    const calories = toOptionalNumber(itemRecord.calories ?? nutritionRecord?.calories);
    const protein = toOptionalNumber(itemRecord.protein ?? nutritionRecord?.protein);
    const carbs = toOptionalNumber(itemRecord.carbs ?? nutritionRecord?.carbs ?? nutritionRecord?.carbohydrate);
    const fat = toOptionalNumber(itemRecord.fat ?? nutritionRecord?.fat ?? nutritionRecord?.total_fat);
    const hasMacroInfo = [calories, protein, carbs, fat].some((value) => value !== undefined);
    const factorLabels = [
      formatFactor('Ret', itemRecord.retentionFactor ?? itemRecord.retention_factor),
      formatFactor('Yield', itemRecord.yieldFactor ?? itemRecord.yield_factor),
    ].filter((label): label is string => Boolean(label));
    const description = typeof itemRecord.description === 'string'
      ? itemRecord.description.trim()
      : '';
    const clinicalWarnings = Array.isArray(itemRecord.clinicalWarnings)
      ? itemRecord.clinicalWarnings.filter((warning): warning is Record<string, unknown> => Boolean(warning && typeof warning === 'object'))
      : [];
    
    return (
      <li
        key={itemId || `index-${index}`}
        className={`p-2 transition-all ${
          isAddingItem 
            ? `bg-gradient-to-r ${colorClasses.bgGradient} cursor-wait opacity-60` 
            : isSelected
            ? `${colorClasses.bg100} border-l-4 ${colorClasses.border500}`
            : `${colorClasses.hoverBg50} cursor-pointer`
        }`}
        data-search-item-id={domMetadata.id}
        data-search-item-type={domMetadata.type}
        data-search-item-source={domMetadata.source}
        data-search-item-provider={domMetadata.provider}
        data-search-item-is-cached={String(domMetadata.isCached)}
        data-search-item-source-id={domMetadata.sourceId}
        data-search-item-internal-id={domMetadata.internalId}
        data-search-item-spoonacular-id={domMetadata.spoonacularId}
        onClick={() => {
          if (!isAddingItem && !isAdding) {
            onSelect(item);
            setSelectedIndex(-1);
          }
        }}
        onMouseEnter={() => setSelectedIndex(index)}
      >
        <div className="flex gap-2 items-center relative">
          {imageSrc && (
            <img
              src={imageSrc}
              alt={String(displayName ?? 'Ingredient')}
              className="h-10 w-10 rounded object-cover border border-gray-200 shrink-0"
              onError={(event) => {
                event.currentTarget.style.display = 'none';
              }}
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-medium text-gray-900 truncate">
                {displayName}
              </p>
            </div>
            {description ? (
              <p className="mt-0.5 line-clamp-2 text-xs leading-4 text-gray-500">
                {description}
              </p>
            ) : null}
            {hasMacroInfo && (
              <div className="mt-0.5 flex gap-2 text-xs text-gray-600">
                <span>Kcal: {Math.round(calories ?? 0)}</span>
                <span>P: {Math.round(protein ?? 0)}g</span>
                <span>C: {Math.round(carbs ?? 0)}g</span>
                <span>F: {Math.round(fat ?? 0)}g</span>
              </div>
            )}
            {factorLabels.length > 0 ? (
              <div className="mt-1 flex flex-wrap gap-1">
                {factorLabels.map((label) => (
                  <span
                    key={label}
                    className="inline-flex h-5 items-center rounded-md bg-gray-100 px-1.5 text-xs font-medium text-gray-600"
                  >
                    {label}
                  </span>
                ))}
              </div>
            ) : null}
            <RecipeAllergenCard
              allergens={itemRecord.allergens}
              compact
              maxVisible={3}
              className="mt-1"
            />
            <SpoonacularTagSummary item={itemRecord} className="mt-1" />
            {clinicalWarnings.map((warning, warningIndex) => (
              <div
                key={`${String(warning.ruleId ?? 'clinical-warning')}-${warningIndex}`}
                className="mt-1 flex items-start gap-1 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs leading-4 text-amber-900"
              >
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{String(warning.message ?? 'Review this item for the client’s clinical condition.')}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="hidden" data-search-item-metadata="true" aria-hidden="true">
          <span data-meta-key="id">{domMetadata.id}</span>
          <span data-meta-key="type">{domMetadata.type}</span>
          <span data-meta-key="source">{domMetadata.source}</span>
          <span data-meta-key="provider">{domMetadata.provider}</span>
          <span data-meta-key="isCached">{String(domMetadata.isCached)}</span>
          <span data-meta-key="sourceId">{domMetadata.sourceId}</span>
          <span data-meta-key="internalId">{domMetadata.internalId}</span>
          <span data-meta-key="spoonacularId">{domMetadata.spoonacularId}</span>
          <span data-meta-key="retentionFactor">
            {String(itemRecord.retentionFactor ?? itemRecord.retention_factor ?? '')}
          </span>
          <span data-meta-key="yieldFactor">
            {String(itemRecord.yieldFactor ?? itemRecord.yield_factor ?? '')}
          </span>
        </div>
      </li>
    );
  };

  const defaultRenderEmptyState = () => (
    <p className="p-3 text-xs text-gray-500 text-center">
      {emptyStateText}
    </p>
  );

  const defaultRenderLoadingState = () => (
    <div className="p-2 space-y-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex gap-2 p-2">
          <Skeleton className="w-12 h-12 rounded" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-2 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div ref={containerRef} className={`relative ${containerClassName}`}>
      <div className={`relative ${className}`}>
        <Input
          placeholder={placeholder}
          value={value}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled || isAnyItemAdding}
          className={`h-10 text-sm text-left border-2 [&_.MuiOutlinedInput-input]:pl-10 [&_.MuiOutlinedInput-input]:pr-8 [&_.MuiOutlinedInput-input]:text-left ${colorClasses.border200} ${colorClasses.focusBorder} focus:ring-2 ${colorClasses.focusRing} ${colorClasses.bg50}/50 ${isAnyItemAdding ? 'opacity-60' : ''} ${inputClassName}`}
        />
        
        <div className="absolute left-3 top-1/2 -translate-y-1/2">
          {isSearching ? (
            <Loader2 className={`h-4 w-4 animate-spin ${colorClasses.text600}`} />
          ) : isAnyItemAdding ? (
            <Loader2 className={`h-4 w-4 animate-spin ${colorClasses.text600}`} />
          ) : onSearchSubmit ? (
            <button
              type="button"
              aria-label="Search"
              title="Search"
              disabled={!canSubmitSearch}
              onClick={onSearchSubmit}
              className="flex h-5 w-5 items-center justify-center disabled:cursor-default disabled:opacity-60"
            >
              <Search className={`h-4 w-4 ${colorClasses.text500}`} />
            </button>
          ) : (
            <Search className={`h-4 w-4 ${colorClasses.text500}`} />
          )}
        </div>
        
        {showClearButton && value && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {showResults && value.length >= minSearchLength && (
        portalResults && typeof document !== 'undefined'
          ? createPortal(
              <div className="fixed z-[10050]" style={portalStyle}>
                {renderDropdownResults()}
              </div>,
              document.body
            )
          : (
              <div className={`${dropdownPositionClassName} mt-2`}>
                {renderDropdownResults()}
              </div>
            )
      )}
    </div>
  );
}
